// The install page (B14; since 19 Oct the Play Store comes first, with this page as the way in
// until the listing exists and as a fallback after: app/distribution.ts) and the privacy page
// (B12; docs/PURPOSE.md "Children's privacy", and the privacy policy URL the Play listing names).
// Both open before anyone has picked who they are, use the app's own chrome, and work offline
// after the first load (the shell is precached). Reached from the join screen and from Settings;
// plain language for a parent deciding.
import { useEffect, useState } from 'react'
import { PLAY_STORE_URL, SUPPORT_CONTACT } from '../app/distribution'
import { goTab } from '../app/router'
import { useSession } from '../data/session'
import { t } from '../strings/en'

/** Chrome's install prompt, when it offers one (Android Chrome, desktop Chrome/Edge). */
interface InstallPrompt extends Event {
  prompt(): Promise<void>
}

let deferred: InstallPrompt | null = null
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred = e as InstallPrompt
  })
}

function Back() {
  return (
    <button className="x-btn press" aria-label={t.info.back} onClick={() => goTab('map')}>
      ←
    </button>
  )
}

export function InstallPage() {
  const [canPrompt, setCanPrompt] = useState(!!deferred)
  const [code, setCode] = useState('')
  // Not known until the session is restored: the Join box waits rather than flashing.
  const signedIn = useSession((s) => !s.ready || !!s.person)
  // A link out of the app is for grown-ups only (B3, Play Families): not on a child's phone.
  const child = useSession((s) => s.person?.kind === 'child')
  const installed = typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches
  useEffect(() => {
    const on = () => setCanPrompt(true)
    window.addEventListener('beforeinstallprompt', on)
    return () => window.removeEventListener('beforeinstallprompt', on)
  }, [])
  const c = code.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
  return (
    <main className="screen info-page">
      <header className="info-head">
        <Back />
        <h1 className="h1">{t.info.installTitle}</h1>
      </header>
      <div className="panel sheet-panel info-text">
        <p>{t.info.what}</p>
        <p>{t.info.whatSub}</p>
      </div>
      <p className="mono sheet-sub">{t.info.howTitle}</p>
      <div className="panel sheet-panel info-text">
        {installed ? <p>{t.info.installed}</p> : null}
        {installed ? <p>{t.info.anotherPhone(!!PLAY_STORE_URL)}</p> : null}
        {PLAY_STORE_URL && !installed && !child ? (
          <a className="btn y press" href={PLAY_STORE_URL} target="_blank" rel="noopener">
            {t.info.getOnPlay}
          </a>
        ) : null}
        {PLAY_STORE_URL && !installed ? <p>{t.info.orFromPage}</p> : null}
        {canPrompt && !installed ? (
          <button
            className="btn y press"
            onClick={() => {
              void deferred?.prompt()
              deferred = null
              setCanPrompt(false)
            }}
          >
            {t.info.installNow}
          </button>
        ) : null}
        {/* Inside the installed app (a home-screen icon or the Play Store's) the steps are done. */}
        {installed ? null : (
          <ol>
            {t.info.steps(canPrompt).map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        )}
        <p className="caption">{t.info.offline(!!PLAY_STORE_URL)}</p>
      </div>
      {/* Someone signed in is already in a family: joining one is for a new phone (WI-22). */}
      {signedIn ? null : (
        <>
          <p className="mono sheet-sub">{t.info.joinTitle}</p>
          <div className="panel sheet-panel info-text">
            <p>{t.info.joinSub}</p>
            <input className="join-code" aria-label={t.info.joinCode} value={code} maxLength={7} autoCapitalize="characters" autoComplete="off" spellCheck={false} placeholder={t.join.placeholder} onChange={(e) => setCode(e.target.value.toUpperCase())} />
            <button className="btn press" disabled={c.length !== 6} onClick={() => location.assign(`/join#${c}`)}>
              {t.join.go}
            </button>
          </div>
        </>
      )}
      <button className="link info-link" onClick={() => goTab('privacy')}>
        {t.info.privacyLink}
      </button>
    </main>
  )
}

export function PrivacyPage() {
  return (
    <main className="screen info-page">
      <header className="info-head">
        <Back />
        <h1 className="h1">{t.info.privacyTitle}</h1>
      </header>
      <div className="panel sheet-panel info-text">
        <p>{t.info.privacyIntro}</p>
      </div>
      {t.info.privacy.map((s) => (
        <section key={s.title}>
          <p className="mono sheet-sub">{s.title}</p>
          <div className="panel sheet-panel info-text">
            <ul>
              {s.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </div>
        </section>
      ))}
      {SUPPORT_CONTACT ? (
        <div className="panel sheet-panel info-text">
          <p>{t.info.contact(SUPPORT_CONTACT)}</p>
        </div>
      ) : null}
      <button className="link info-link" onClick={() => goTab('install')}>
        {t.info.installLink}
      </button>
    </main>
  )
}
